import React, { createContext, ReactNode, useContext, useState } from "react";
import { Modal, ModalDialog, Typography, Button, Box } from "@mui/joy";

interface ConfirmationOptions {
  title: string;
  message: string;
  onConfirm: () => void; // Action to run on confirm
  onCancel?: () => void; // Action to run on cancel (optional)
}

interface ConfirmationContextType {
  confirm: (options: ConfirmationOptions) => void;
}

const ConfirmationContext = createContext<ConfirmationContextType | null>(null);

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error("useConfirmation must be used within a ConfirmationProvider");
  }
  return context;
};

interface ConfirmationProviderProps {
  children: ReactNode;
}

export const ConfirmationProvider: React.FC<ConfirmationProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmationOptions | null>(null);

  const confirm = (newOptions: ConfirmationOptions) => {
    setOptions(newOptions);
    setIsOpen(true);
  };

  const handleConfirm = () => {
    if (options?.onConfirm) {
      options.onConfirm();
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    if (options?.onCancel) {
      options.onCancel();
    }
    setIsOpen(false);
  };

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      {isOpen && (
        <Modal open={isOpen} onClose={handleCancel}>
          <ModalDialog>
            <Typography level="h4" fontWeight="bold">
              {options?.title}
            </Typography>
            <Typography sx={{ mt: 1, mb: 2 }}>{options?.message}</Typography>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="plain" onClick={handleCancel}>
                Annuler
              </Button>
              <Button variant="solid" color="danger" onClick={handleConfirm}>
                Confirmer
              </Button>
            </Box>
          </ModalDialog>
        </Modal>
      )}
    </ConfirmationContext.Provider>
  );
};
